import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Sparkles } from 'lucide-react';
import { fetchSettings, resolveImageUrl } from '../services/api';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetchSettings()
      .then((s) => setLogoUrl(s?.logo_url || ''))
      .catch(() => setLogoUrl(''));
  }, []);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Crystals', path: '/crystals' },
    { name: 'Blog', path: '/blog' },
    { name: 'Videos', path: '/videos' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'About', path: '/about' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        scrolled
          ? 'bg-[#0a1612]/90 backdrop-blur-xl border-b border-amber-600/20 shadow-2xl'
          : 'bg-gradient-to-b from-[#0a1612]/70 to-transparent backdrop-blur-md'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
            {logoUrl ? (
              <img
                src={resolveImageUrl(logoUrl)}
                alt="The Divine7777"
                className="w-14 h-14 object-contain rounded-lg group-hover:scale-110 transition-transform duration-500"
                data-testid="header-logo-image"
              />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-700 via-emerald-600 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-500 mystical-glow-gold border border-amber-500/40">
                <Sparkles className="w-7 h-7 text-amber-100" />
              </div>
            )}
            <div className="hidden sm:block">
              <span className="block text-xl font-bold text-amber-100 tracking-wider" style={{ fontFamily: 'Cinzel, serif' }}>
                The Divine7777
              </span>
              <span className="block text-xs text-emerald-400/80 tracking-widest uppercase">
                Mystical Tarot
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-5 py-2 text-sm font-medium transition-all duration-300 tracking-wider uppercase ${
                  isActive(item.path)
                    ? 'text-amber-400'
                    : 'text-amber-100/80 hover:text-amber-300'
                }`}
                style={{ fontFamily: 'Cinzel, serif' }}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              className="btn-gold px-6"
              onClick={() => window.open('https://wa.me/919681424041', '_blank')}
              data-testid="header-book-btn"
            >
              Book Reading
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-amber-100 hover:text-amber-400 hover:bg-emerald-900/40 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-amber-600/20">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-2 rounded-md text-base font-medium transition-colors uppercase tracking-wider ${
                  isActive(item.path)
                    ? 'text-amber-400 bg-emerald-900/40'
                    : 'text-amber-100/80 hover:text-amber-400 hover:bg-emerald-900/30'
                }`}
                style={{ fontFamily: 'Cinzel, serif' }}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-2">
              <Button
                className="btn-gold w-full"
                onClick={() => window.open('https://wa.me/919681424041', '_blank')}
              >
                Book Reading
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
